import React, { useState, useMemo, useCallback, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';

// COMPONENTES DE ARQUITECTURA MODULAR COMUNES
import SortableHeader from '../common/SortableHeader.jsx';
import PaginatorFooter from '../common/PaginatorFooter';
import ColumnSelector from '../common/ColumnSelector';
import TableContainer from '../common/TableContainer';
import Icons from '../common/Icons';

// UI COMPARTIDA Y MODALES
import FiltrosDinamicos from '../../../ui/FiltrosDinamicos.jsx';
import AuthModal from '../../../ui/AuthModal';

// COMPONENTES Y CONTROLADORES PROPIOS DEL MÓDULO
import ModalCalificarClienteCroselling from './ModalCalificarClienteCroselling';
import DetalleClienteCroselling from './DetalleClienteCroselling';
import { useClientesCrosellingTable } from './useClientesCrosellingTable';
import { usePermissions } from '../../../hooks/usePermissions.js';

// ESTILOS CENTRALIZADOS
import { estadoGestionCroselling } from '../../../../utils/styles';

const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '-';
    if (typeof dateStr === 'string' && dateStr.includes('-')) {
        const parts = dateStr.split('T')[0].split('-');
        if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
};

const TableRow = memo(({ item, isSelected, columnVisibility, onSelect, onAction, isFullUser }) => {
    return (
        <tr 
            onClick={() => onSelect(item.id)} 
            className={`group cursor-pointer transition-all duration-75 border-b border-slate-100 ${
                isSelected ? 'bg-blue-100/60' : 'hover:bg-slate-50'
            }`}
        >
            {columnVisibility.fechaCarga && (
                <td className="p-3 px-3 border-b border-r border-slate-100 text-center text-xs text-slate-500 font-mono tabular-nums">
                    {formatDateDisplay(item.fechaCarga)}
                </td>
            )}
            {columnVisibility.displayNombre && (
                <td className="p-3 px-4 border-b border-r border-slate-100 text-sm font-bold text-slate-900 truncate uppercase text-left" title={item.displayNombre}>
                    {item.displayNombre}
                </td>
            )}
            {columnVisibility.displayDoc && (
                <td className="p-3 px-3 border-b border-r border-slate-100 text-center text-xs font-mono text-slate-500">
                    {item.displayDoc}
                </td>
            )}
            {columnVisibility.productosDisplay && (
                <td className="p-3 px-4 border-b border-r border-slate-100 text-left text-xs font-bold text-blue-700 truncate" title={item.productosDisplay}>
                    {item.productosDisplay}
                </td>
            )}
            {columnVisibility.estadoGestion && (
                <td className="p-3 px-2 border-b border-r border-slate-100 text-center">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase border ${estadoGestionCroselling(item.estadoGestion)}`}>
                        {item.estadoGestion || '-'}
                    </span>
                </td>
            )}
            {columnVisibility.responsableAsignado && (
                <td className="p-3 px-2 border-b border-r border-slate-100 text-center text-xs font-bold text-slate-700">
                    {item.responsableAsignado || '-'}
                </td>
            )}
            
            {columnVisibility.fechaProximoContacto && (
                <td className="p-3 px-2 border-b border-r border-slate-100 text-center">
                    {item.fechaProximoContacto ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-black uppercase tracking-wider bg-red-50 text-red-700 border border-red-200 shadow-sm">
                            📅 {formatDateDisplay(item.fechaProximoContacto)}
                        </span>
                    ) : (
                        <span className="text-slate-300 text-xs font-bold">-</span>
                    )}
                </td>
            )}

            {columnVisibility.canal && (
                <td className="p-3 px-4 border-b border-r border-slate-100 text-left text-[11px] font-bold text-slate-500 uppercase truncate" title={item.canal}>
                    {item.canal || '-'}
                </td>
            )}
            {columnVisibility.canalNombre && (
                <td className="p-3 px-4 border-b border-r border-slate-100 text-left text-[11px] font-bold text-slate-700 uppercase truncate" title={item.canalNombre}>
                    {item.canalNombre || '-'}
                </td>
            )}
            {columnVisibility.subcanalNombre && (
                <td className="p-3 px-4 border-b border-r border-slate-100 text-left text-[11px] font-bold text-slate-500 uppercase truncate" title={item.subcanalNombre}>
                    {item.subcanalNombre || '-'}
                </td>
            )}

            <td className="p-3 px-2 border-b border-slate-100 text-center sticky right-0 z-20 bg-white group-hover:bg-slate-50 border-l border-slate-100 shadow-[-10px_0_15px_-5px_rgba(0,0,0,0.03)]">
                <div className="flex justify-center items-center gap-1.5">
                    <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'VIEW'); }} className="p-1 text-slate-400 hover:text-blue-600 transition-all active:scale-90 cursor-pointer" title="Ver Detalle y Bitácora"><Icons.Eye /></button>
                    {isFullUser && (
                        <>
                            <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'EDIT'); }} className="p-1 text-slate-400 hover:text-orange-600 transition-all active:scale-90 cursor-pointer" title="Editar"><Icons.Edit /></button>
                            <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'DELETE'); }} className="p-1 text-slate-400 hover:text-red-600 transition-all active:scale-90 cursor-pointer" title="Eliminar"><Icons.Trash /></button>
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
});
TableRow.displayName = 'TableRow';

const ClientesCroselling = () => {
    const navigate = useNavigate();
    const [showCalificarModal, setShowCalificarModal] = useState(false);

    const {
        view, setView,
        loading,
        editingRecord,
        detailRecord, setDetailRecord,
        authModalConfig, setAuthModalConfig,
        tableState,
        itemsTable,
        handleAction: originalHandleAction,
        confirmDelete,
        handleSuccess
    } = useClientesCrosellingTable();

    const { isFullUser, loadingAuth } = usePermissions();

    const {
        sortConfig,
        columnVisibility,
        currentPage, setCurrentPage,
        selectedRowId, setSelectedRowId,
        filterValues, filteredData, paginatedData, totalPages,
        handleSort, toggleColumn, handleFilterChange
    } = tableState;

    // SINCRONIZACIÓN EN TIEMPO REAL DEL REGISTRO EN DETALLE
    useEffect(() => {
        if (view === 'detail' && detailRecord?.id) {
            const updated = itemsTable.find(item => item.id === detailRecord.id);
            if (updated) {
                setDetailRecord(updated);
            }
        }
    }, [itemsTable, view, detailRecord?.id, setDetailRecord]);

    const columnLabels = {
        fechaCarga: "Fecha Carga", displayNombre: "Cliente / Prospecto", displayDoc: "CUIT / DNI",
        productosDisplay: "Productos Sugeridos", estadoGestion: "Estado Gestión",
        responsableAsignado: "Responsable", fechaProximoContacto: "Agenda Contacto",
        canal: "Canal / Org", canalNombre: "Nombre Canal", subcanalNombre: "Subcanal / Asociado"
    };

    const filterConfig = useMemo(() => {
        const getUnique = (key, data = itemsTable) => [...new Set(data.map(item => item[key]).filter(Boolean))].sort().map(v => ({label: v, value: v}));
        return [
            { name: 'search', label: 'Buscador General', type: 'text', placeholder: 'Cliente, CUIT, Producto...', className: 'w-80' },
            { name: 'estadoGestion', label: 'Estado Gestión', type: 'select', isMulti: true, options: getUnique('estadoGestion') },
            { name: 'responsableAsignado', label: 'Responsable', type: 'select', isMulti: true, options: getUnique('responsableAsignado') },
            { name: 'canal', label: 'Canal', type: 'select', isMulti: true, options: getUnique('canal') },
            { name: 'canalNombre', label: 'Nombre Canal', type: 'select', isMulti: true, options: getUnique('canalNombre') },
            { name: 'subcanalNombre', label: 'Subcanal', type: 'select', isMulti: true, options: getUnique('subcanalNombre') }
        ];
    }, [itemsTable]);

    const handleAction = useCallback((item, type) => {
        if (type === 'EDIT') {
            originalHandleAction(item, type);
            setShowCalificarModal(true);
        } else {
            originalHandleAction(item, type);
        }
    }, [originalHandleAction]);

    const handleOpenCreateModal = useCallback(() => {
        originalHandleAction(null, 'CREATE_CLEAR');
        setShowCalificarModal(true);
    }, [originalHandleAction]);

    const handleCloseModal = useCallback(() => {
        setShowCalificarModal(false);
        originalHandleAction(null, 'CREATE_CLEAR');
    }, [originalHandleAction]);

    // REDIRECCIÓN DIRECTA A LA FICHA DE DETALLE AL GUARDAR DESDE EL MODAL
    const handleModalSuccess = useCallback((savedRecord) => {
        handleSuccess();
        setShowCalificarModal(false);
        originalHandleAction(null, 'CREATE_CLEAR');

        if (savedRecord) {
            setDetailRecord(savedRecord);
            setView('detail');
        }
    }, [handleSuccess, originalHandleAction, setDetailRecord, setView]);

    if (loading || loadingAuth) return (
        <div className="flex items-center justify-center h-screen bg-white text-slate-400 font-bold uppercase tracking-widest text-[10px] animate-pulse">
            Sincronizando tablero de clientes calificados...
        </div>
    );

    // VISTA DE DETALLE CON SINCRONIZACIÓN REACTIVA
    if (view === 'detail' && detailRecord) {
        return (
            <div className="h-screen w-full bg-[#f3f4f8] overflow-y-auto animate-fade-in text-left">
                <DetalleClienteCroselling 
                    record={detailRecord} 
                    onBack={() => { setDetailRecord(null); setView('list'); }} 
                    onEdit={(rec) => {
                        handleAction(rec, 'EDIT');
                    }}
                />

                <ModalCalificarClienteCroselling 
                    show={showCalificarModal} 
                    onClose={handleCloseModal} 
                    onSuccess={handleModalSuccess} 
                    initialData={editingRecord} 
                />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen w-full bg-white text-slate-900 overflow-hidden antialiased font-sans text-left">
            <div className="h-1 bg-blue-600 w-full shrink-0"></div>
            
            <header className="h-20 shrink-0 bg-white border-b border-slate-200 px-8 flex items-center justify-between z-[110] relative shadow-sm">
                <div className="flex items-center gap-6">
                    <button 
                        onClick={() => navigate('/Cpanel')} 
                        className="p-3 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-2xl transition-all cursor-pointer"
                        title="Volver a Cpanel"
                    >
                        <Icons.ArrowLeft />
                    </button>
                    <div>
                        <h2 className="text-xl font-black text-slate-800 tracking-tight leading-tight uppercase italic">Clientes Calificados (Cross-Selling)</h2>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Matriz de oportunidades y productos sugeridos</p>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <ColumnSelector columnVisibility={columnVisibility} columnLabels={columnLabels} toggleColumn={toggleColumn} />
                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-2xl p-1 pr-5 shadow-inner">
                        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-xl text-lg font-black shadow-md">{filteredData.length}</div>
                        <span className="text-[10px] font-bold uppercase text-slate-500">Oportunidades</span>
                    </div>

                    <button 
                        onClick={handleOpenCreateModal} 
                        className="bg-blue-600 text-white px-5 py-2.5 rounded-xl hover:bg-blue-700 text-sm font-bold shadow-lg active:scale-95 flex items-center gap-2 cursor-pointer"
                    >
                        <Icons.Plus /> Calificar Cliente
                    </button>
                </div>
            </header>

            <FiltrosDinamicos config={filterConfig} onFilterChange={handleFilterChange} defaultValues={filterValues} />

            <main className="flex-grow overflow-hidden flex flex-col bg-white">
                <TableContainer minWidth="2200px">
                    <thead>
                        <tr className="text-white">
                            <SortableHeader label="Fecha Carga" sortKey="fechaCarga" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-28 text-center" />
                            <SortableHeader label="Cliente / Prospecto" sortKey="displayNombre" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-72 text-left" />
                            <SortableHeader label="CUIT / DNI" sortKey="displayDoc" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Productos Sugeridos" sortKey="productosDisplay" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-64 text-left" />
                            <SortableHeader label="Estado Gestión" sortKey="estadoGestion" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="Responsable" sortKey="responsableAsignado" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="Agenda Contacto" sortKey="fechaProximoContacto" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="Canal / Org" sortKey="canal" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-left" />
                            <SortableHeader label="Nombre Canal" sortKey="canalNombre" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-48 text-left" />
                            <SortableHeader label="Subcanal / Asociado" sortKey="subcanalNombre" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-48 text-left" />
                            <th className="w-28 p-4 text-center sticky right-0 top-0 z-50 bg-slate-900 border-b border-r border-slate-700 uppercase tracking-wider text-[10px] font-bold text-white">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {paginatedData.map((item) => (
                            <TableRow 
                                key={item.id} 
                                item={item} 
                                isSelected={selectedRowId === item.id} 
                                columnVisibility={columnVisibility} 
                                onSelect={setSelectedRowId} 
                                onAction={handleAction} 
                                isFullUser={isFullUser} 
                            />
                        ))}
                    </tbody>
                </TableContainer>
            </main>

            <PaginatorFooter 
                currentPage={currentPage} 
                totalPages={totalPages} 
                currentLength={paginatedData.length} 
                totalLength={filteredData.length} 
                onPageChange={setCurrentPage} 
                label="oportunidades" 
                accentClass="text-blue-900 bg-blue-50 border-blue-100" 
            />

            <ModalCalificarClienteCroselling 
                show={showCalificarModal} 
                onClose={handleCloseModal} 
                onSuccess={handleModalSuccess} 
                initialData={editingRecord} 
            />

            <AuthModal 
                isOpen={authModalConfig.open} 
                onClose={() => setAuthModalConfig({ open: false, type: '', item: null })} 
                onConfirm={confirmDelete} 
                title="Eliminar Registro de Cross-selling" 
                message="¿Estás seguro que deseas descalificar/borrar esta oportunidad? Esta acción eliminará el historial de la tabla." 
            />
        </div>
    );
};

export default ClientesCroselling;